/**
 * Visitor behavioral events — CDP Phase 1 (FLO-547 / FLO-544, design in
 * docs/CDP_RESEARCH.md §3).
 *
 * Emits the SDK's slice of the closed VisitorEventName allowlist
 * (PAGE_VIEWED, PAGE_SUMMARY, WIDGET_*, VOICE_*, BOOKING_CTA_CLICKED) to
 * `POST /sites/web-agent/events` (contract C1) through the shared
 * {@link EventQueue}. Server-derived events (CONVERSATION_*, EMAIL_CAPTURED,
 * DEMO_*) are emitted by floe-api, never from here.
 *
 * ## The visitor token (contract C1)
 * `floe_wa_visitor` in localStorage (WA_VISITOR_KEY, owned by the shared
 * coordinator visitor-token.ts — the SAME store and single-flight latch the
 * FLO-544 conversation open/resume path uses, so events, nudges and resume
 * share ONE WebVisitor identity) is the SERVER-minted `WebVisitor` token — the
 * canonical anonymous person key (identity ladder rung 4). It lives beside
 * `floe_anonymous_id` (same storage pattern) but is a different thing: the
 * anonymous id is a client-generated EndUser-resolve hint; the visitor token
 * is issued by floe-api and joins events, funnel, nudges and conversation
 * resume.
 *
 * Bootstrap must be an explicit fetch — sendBeacon can't read responses — so
 * the first boot without a token POSTs the boot events and persists
 * `data.visitorToken` from the response envelope. Every later flush includes
 * the stored token and can ride a beacon.
 *
 * ## Consent (contract C5)
 * `Site.eventCollectionEnabled` arrives via validate-client-key. When false
 * the client never emits (and the server 200-drops anything an older SDK
 * still sends). Nothing is emitted before the SDK has validated, either:
 * emission starts at {@link VisitorEventsClient.bootstrap}.
 *
 * No PII in event props (design §3): paths only (no query strings), no email,
 * and booking URLs are stripped to origin+path before they ride an event —
 * Customer-configured booking URLs can carry provider query parameters.
 *
 * ## The visitor journey (FLO-1096)
 * With page engagement allowed (see {@link VisitorEventsClientOptions}), a
 * PAGE_VIEWED also carries the page title and, on the first view of a page
 * load, the referrer's HOST and the five UTM tags; every other query parameter
 * is left behind. Each page visit then ends in ONE PAGE_SUMMARY
 * (page-engagement.ts): it is queued when the visitor moves to another page,
 * and sent at once when the page is hidden. A later send for the same page
 * visit reuses its `clientEventId`, and floe-api replaces the row.
 */

import { EventQueue, sendEventBatch } from './event-queue';
import { logger as defaultLogger, Logger } from './logger';
import {
  PAGE_ENGAGEMENT_CAPS,
  createPageEngagementTracker,
  pageIgnored,
  pageTitle,
} from './page-engagement';
import type { PageEngagement, PageEngagementTracker } from './page-engagement';
import {
  beginTokenAcquisition,
  getStoredVisitorToken,
  pendingTokenAcquisition,
  storeVisitorToken,
} from './visitor-token';

// The token store + single-flight acquisition latch live in visitor-token.ts,
// SHARED with the conversation open/resume path (wa-resume.ts) so the two
// token-less paths of a first visit can never each mint their own WebVisitor.
// Re-exported here because this module is where consumers historically got them.
export { getStoredVisitorToken, storeVisitorToken } from './visitor-token';

/**
 * A bootstrap mint is analytics, never permission to hold the shared visitor
 * identity coordinator forever. Keep this aligned with the conversation-open
 * budget: if the endpoint or response body wedges, abort and release the latch
 * so a later interaction can retry cleanly.
 */
export const VISITOR_TOKEN_MINT_TIMEOUT_MS = 4000;

/** The SDK-emitted subset of the server's VisitorEventName enum (contract C1).
 *  Unknown names are dropped (not 400'd) at ingest, so this list can trail
 *  the server's without breaking anything — but never lead it. */
export type SdkVisitorEventName =
  | 'PAGE_VIEWED'
  // One per page visit: visible time, deepest scroll, sections read, link and
  // button clicks, form submits (FLO-1096). Never a field value.
  | 'PAGE_SUMMARY'
  | 'WIDGET_LOADED'
  | 'WIDGET_OPENED'
  | 'WIDGET_CLOSED'
  | 'VOICE_STARTED'
  // The click → first-spoken-word critical path, one event per session, emitted
  // when the greeting actually lands (voice-startup-timing.ts). Distinct from
  // VOICE_STARTED, which means "the visitor's MIC went live" and never fires at
  // all for a keyboard-mode session — the exact session shape the 2026-09-12
  // latency forensics were run on.
  | 'VOICE_READY'
  | 'VOICE_ENDED'
  | 'BOOKING_CTA_CLICKED';

/** One item of the C1 ingest batch. */
export interface VisitorEventItem {
  clientEventId: string;
  name: SdkVisitorEventName;
  props?: Record<string, unknown>;
  page?: string;
  ts: number;
}

/**
 * What the visitor has done so far on the page they are on (FLO-1096), in the
 * shape of a PAGE_SUMMARY event: its path and the summary's props.
 */
export interface CurrentPageSummary {
  page: string;
  /** The summary, and the id of the PAGE_VIEWED it belongs to. */
  props: PageEngagement & { viewId: string };
}

/**
 * A short per-event idempotency key. The server dedupes on
 * (siteId, clientEventId), so this needs to be unique across ALL of a site's
 * events, not just this visitor's — epoch millis + 8 base36 chars keeps
 * collisions out of birthday range at any plausible volume while staying
 * short enough for a beacon payload.
 */
export function newClientEventId(): string {
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

/** Booking provider inferred from the CTA's URL host. Never throws. */
export function inferBookingProvider(url: string): 'cal.com' | 'calendly' | 'other' {
  try {
    const host = new URL(url).hostname.toLowerCase();
    if (host === 'cal.com' || host.endsWith('.cal.com')) return 'cal.com';
    if (host === 'calendly.com' || host.endsWith('.calendly.com')) return 'calendly';
  } catch {
    /* unparseable → other */
  }
  return 'other';
}

/** origin+path only — booking links carry prospect email as query params. */
export function stripUrlForEvent(url: string): string {
  try {
    const u = new URL(url);
    return `${u.origin}${u.pathname}`;
  } catch {
    // Not parseable as absolute — drop anything after ? or # rather than
    // forwarding a query string we can't inspect.
    return url.split(/[?#]/)[0];
  }
}

/**
 * A page visit sends no summary until it has this much visible time, and no
 * re-send until it has gained this much more. `pagehide` and
 * `visibilitychange` fire milliseconds apart on a real unload; without the
 * second half every unload would send the summary twice.
 */
export const PAGE_SUMMARY_MIN_ACTIVE_MS = 1000;

/** The query keys read off a landing URL, and the prop each becomes. No other
 *  key is ever read. */
const UTM_PARAMS = [
  ['utm_source', 'utmSource'],
  ['utm_medium', 'utmMedium'],
  ['utm_campaign', 'utmCampaign'],
  ['utm_term', 'utmTerm'],
  ['utm_content', 'utmContent'],
] as const;

/** What a PAGE_VIEWED may say about the page beyond its path. */
export interface PageViewProps {
  title?: string;
  referrerHost?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
}

const clip = (text: string, maxChars: number): string =>
  [...text.trim()].slice(0, maxChars).join('');

/**
 * Where a page load came from: the referrer's host when it is another site,
 * and the five UTM tags. Only the host of the referrer is kept (its path and
 * query can name a person), and only those five query keys are read.
 */
export const landingProps = (pageUrl: string, referrer: string): PageViewProps => {
  const props: PageViewProps = {};
  try {
    const page = new URL(pageUrl);
    for (const [param, prop] of UTM_PARAMS) {
      const value = clip(page.searchParams.get(param) ?? '', PAGE_ENGAGEMENT_CAPS.textChars);
      if (value) {
        props[prop] = value;
      }
    }
    const from = referrer ? new URL(referrer) : null;
    if (from && /^https?:$/u.test(from.protocol) && from.hostname !== page.hostname) {
      props.referrerHost = from.hostname;
    }
  } catch {
    /* an unparseable URL says nothing */
  }
  return props;
};

/** `data-floe-ignore` on `<html>` or `<body>`: the host excluded this whole
 *  page, so only its path is sent. An unreadable document counts as ignored. */
const wholePageIgnored = (): boolean => {
  try {
    return pageIgnored();
  } catch {
    return true;
  }
};

/** The page visit a PAGE_SUMMARY is being built for. */
interface PageVisit {
  /** `clientEventId` of the PAGE_VIEWED that opened it. */
  viewId: string;
  /** `clientEventId` of its summary, the same for every re-send. */
  summaryId: string;
  /** The path it was opened on; `location` has moved on by the time an SPA
   *  route change closes it. */
  path: string;
  /** Active time in the last summary sent. */
  sentActiveMs: number;
}

export interface VisitorEventsClientOptions {
  apiUrl: () => string;
  clientKey: () => string;
  /**
   * May the SDK read page engagement (FLO-1096)? Asked LIVE, before a page
   * visit is watched and before each summary is queued. The answer is the
   * site's collection switch AND the visitor's consent
   * (`pageEngagementAllowed` in consent-mode.ts). Absent means NO: a client
   * built without it sends path-only page views and nothing else.
   */
  pageEngagementAllowed?: () => boolean;
  /** Injectable tracker (tests). */
  pageEngagement?: PageEngagementTracker;
  logger?: Logger;
  /** Injectable transports (tests). */
  send?: (url: string, body: string, useBeacon: boolean) => void;
  fetchFn?: typeof fetch;
}

export class VisitorEventsClient {
  private readonly logger: Logger;
  private readonly options: VisitorEventsClientOptions;
  private readonly queue: EventQueue<VisitorEventItem>;
  /** C5 kill switch — validate-client-key truth. Default on (server enforces too). */
  private collectionEnabled = true;
  /** Emission is armed by bootstrap() (i.e. only after validate-client-key). */
  private active = false;
  /** Dedupe consecutive same-path PAGE_VIEWED (a /a → /b → /a all count). */
  private lastPageViewedPath: string | null = null;
  // Token-less sends serialize on THE shared acquisition latch in
  // visitor-token.ts (pendingTokenAcquisition / beginTokenAcquisition) — one
  // latch for this client AND wa-resume's open/resume, not a second one here.
  /** Always-on SPA URL watcher (see {@link startUrlWatch}). */
  private urlWatchTimer: ReturnType<typeof setInterval> | null = null;
  private popstateHandler: (() => void) | null = null;
  /** The client-owned bootstrap mint, if any. Conversation open/resume can own
   *  the module-wide acquisition latch too, so only abort this controller. */
  private mintAbortController: AbortController | null = null;
  /** Invalidates a response that races consent withdrawal or teardown. */
  private mintGeneration = 0;
  /** What the visitor does on the current page (FLO-1096). */
  private readonly engagement: PageEngagementTracker;
  private pageVisit: PageVisit | null = null;
  /** Referrer and campaign ride the FIRST page view of a page load only. */
  private landingReported = false;
  private pageHideHandler: (() => void) | null = null;
  private pageVisibilityHandler: (() => void) | null = null;

  constructor(options: VisitorEventsClientOptions) {
    this.options = options;
    this.logger = options.logger ?? defaultLogger;
    this.engagement = options.pageEngagement ?? createPageEngagementTracker();
    this.queue = new EventQueue<VisitorEventItem>({
      url: () => this.endpoint(),
      buildBody: (events) => {
        const token = getStoredVisitorToken();
        return {
          clientKey: this.options.clientKey(),
          ...(token ? { visitorToken: token } : {}),
          events,
        };
      },
      send: (url, body, useBeacon) => this.send(url, body, useBeacon),
      logger: this.logger,
    });
  }

  private endpoint(): string {
    return `${this.options.apiUrl()}/sites/web-agent/events`;
  }

  /** C5: flip from validate-client-key. Off DROPS anything already queued
   *  (no goodbye flush — consent-off means nothing else leaves the browser). */
  setCollectionEnabled(enabled: boolean): void {
    this.collectionEnabled = enabled;
    if (!enabled) {
      this.active = false;
      this.invalidateMint();
      this.stopUrlWatch();
      // No goodbye summary: the page stops being watched and what it held is
      // dropped with the rest of the queue.
      this.stopPageEngagement();
      this.queue.clear();
      this.queue.dispose();
    }
  }

  /**
   * Arm emission and deliver the boot events (WIDGET_LOADED + the first
   * PAGE_VIEWED). Call ONCE, after validate-client-key succeeded.
   *
   * Without a stored token this is the explicit-fetch bootstrap: the server
   * mints a WebVisitor and returns it in the response envelope
   * ({success, data:{visitorToken}}), which a beacon could never read. With
   * one, the boot events just ride the normal debounced queue.
   */
  async bootstrap(): Promise<void> {
    if (this.active || !this.collectionEnabled || !this.options.clientKey()) return;
    this.active = true;

    const path = this.currentPath();
    this.lastPageViewedPath = path;
    const bootEvents: VisitorEventItem[] = [
      this.buildEvent('WIDGET_LOADED'),
      this.openPageVisit(),
    ];

    if (getStoredVisitorToken()) {
      for (const e of bootEvents) this.queue.enqueue(e);
      return;
    }

    // No token yet, but the OTHER token-less path (wa-resume's conversation
    // open) may already be acquiring one — join it rather than racing a second
    // mint: the boot events ride the queue, whose token-less send re-checks
    // the world once the acquisition settles.
    const pending = pendingTokenAcquisition();
    if (pending) {
      for (const e of bootEvents) this.queue.enqueue(e);
      await pending;
      return;
    }

    // No acquisition anywhere — this explicit fetch IS the shared mint: while
    // it is in flight, any token-less send (ours or wa-resume's open) joins it
    // instead of racing its own minting fetch. Keep the same boot events in the
    // debounced queue too: a failed first fetch can then retry them, while a
    // successful direct mint is safely deduplicated by clientEventId server-side.
    for (const e of bootEvents) this.queue.enqueue(e);
    this.startMint(
      JSON.stringify({ clientKey: this.options.clientKey(), events: bootEvents }),
    );
    await (pendingTokenAcquisition() ?? Promise.resolve());
  }

  /** Queue one event. Inert until bootstrap(), and always inert when C5 is off. */
  track(name: SdkVisitorEventName, props?: Record<string, unknown>): void {
    if (!this.active || !this.collectionEnabled) return;
    this.queue.enqueue(this.buildEvent(name, props));
  }

  /** PAGE_VIEWED with consecutive same-path dedupe (SPA polls re-report). */
  pageViewed(path: string): void {
    if (!this.active || !this.collectionEnabled) return;
    if (path === this.lastPageViewedPath) return;
    this.lastPageViewedPath = path;
    // Close the page being left BEFORE opening the next: its summary is
    // queued first, under the path it was opened on.
    this.queuePageSummary();
    this.queue.enqueue(this.openPageVisit());
  }

  /**
   * FLO-1096: the one gate on reading page engagement. Emission must be armed
   * (validated key, site switch on) AND the host's live predicate must say
   * yes. Positive on purpose: a client nobody gave a predicate reads nothing.
   */
  private pageEngagementAllowed(): boolean {
    return (
      this.active &&
      this.collectionEnabled &&
      this.options.pageEngagementAllowed?.() === true
    );
  }

  /**
   * Build the PAGE_VIEWED for the page the visitor is on now, and start
   * watching that page. Without the gate it is the path-only event it always
   * was, and nothing is watched.
   */
  private openPageVisit(): VisitorEventItem {
    if (!this.pageEngagementAllowed() || wholePageIgnored()) {
      this.stopPageEngagement();
      return this.buildEvent('PAGE_VIEWED');
    }
    const view = this.buildEvent('PAGE_VIEWED', { ...this.pageViewProps() });
    this.pageVisit = {
      path: view.page ?? '',
      sentActiveMs: 0,
      summaryId: newClientEventId(),
      viewId: view.clientEventId,
    };
    this.engagement.start();
    this.bindPageHide();
    return view;
  }

  /** The title, plus where the visitor came from on a page load's first view. */
  private pageViewProps(): PageViewProps {
    try {
      const title = pageTitle();
      const landing = this.landingReported
        ? {}
        : landingProps(location.href, document.referrer);
      this.landingReported = true;
      return title ? { ...landing, title } : landing;
    } catch {
      return {};
    }
  }

  /**
   * Queue the current page visit's summary, replacing one still waiting to be
   * sent. Skipped when the visit has gained no visible time since the last
   * send: there is nothing new to say, and the server would ignore it.
   */
  private queuePageSummary(): void {
    const visit = this.pageVisit;
    if (!visit) {
      return;
    }
    if (!this.pageEngagementAllowed()) {
      this.stopPageEngagement();
      return;
    }
    const summary = this.engagement.snapshot();
    if (
      !summary ||
      summary.activeMs - visit.sentActiveMs < PAGE_SUMMARY_MIN_ACTIVE_MS
    ) {
      return;
    }
    visit.sentActiveMs = summary.activeMs;
    this.queue.replaceOrEnqueue(
      {
        clientEventId: visit.summaryId,
        name: 'PAGE_SUMMARY',
        page: visit.path,
        props: { ...summary, viewId: visit.viewId },
        ts: Date.now(),
      },
      (queued) => queued.clientEventId === visit.summaryId,
    );
  }

  /**
   * FLO-1096: what the visitor has done so far on the page they are on, for
   * an agent session that is about to start. A page's summary is otherwise
   * sent only when the page is hidden or left, so the one page a visitor opens
   * the agent on would reach the agent as a path and a title.
   *
   * Same gate and same shape as the queued PAGE_SUMMARY. Nothing is sent or
   * queued here, and the visit's own summary is untouched: the caller places
   * this in the session-start request, where the server cleans it exactly as
   * it cleans the event. Null when nothing is being watched.
   */
  currentPageSummary(): CurrentPageSummary | null {
    const visit = this.pageVisit;
    if (!visit || !this.pageEngagementAllowed()) {
      return null;
    }
    try {
      const summary = this.engagement.snapshot();
      return summary
        ? { page: visit.path, props: { ...summary, viewId: visit.viewId } }
        : null;
    } catch {
      // Analytics must never throw into a session start.
      return null;
    }
  }

  /** The page is being hidden or left: its summary goes out NOW, with
   *  whatever else is queued. There may be no later chance. */
  private flushPageSummary(): void {
    if (!this.pageVisit) {
      return;
    }
    try {
      this.queuePageSummary();
      this.queue.flush(true);
    } catch {
      /* analytics must never throw into the host page */
    }
  }

  private bindPageHide(): void {
    if (this.pageHideHandler || typeof window === 'undefined') {
      return;
    }
    this.pageHideHandler = () => this.flushPageSummary();
    this.pageVisibilityHandler = () => {
      if (document.visibilityState === 'hidden') {
        this.flushPageSummary();
      }
    };
    window.addEventListener('pagehide', this.pageHideHandler);
    document.addEventListener('visibilitychange', this.pageVisibilityHandler);
  }

  /** Stop watching the page and forget the visit, sending nothing. */
  private stopPageEngagement(): void {
    this.pageVisit = null;
    this.engagement.stop();
    if (this.pageHideHandler && typeof window !== 'undefined') {
      window.removeEventListener('pagehide', this.pageHideHandler);
    }
    if (this.pageVisibilityHandler && typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.pageVisibilityHandler);
    }
    this.pageHideHandler = null;
    this.pageVisibilityHandler = null;
  }

  /**
   * Always-on SPA route watcher: a popstate listener plus a modest 2s poll of
   * location.pathname, both feeding {@link pageViewed} (whose consecutive
   * same-path dedupe makes the poll's re-reports — and overlap with
   * PageTracker's own URL-change detection, which only runs once a session is
   * connected — free). Without this, a non-engaged SPA visitor emits exactly
   * one PAGE_VIEWED per full page load. Call after {@link bootstrap};
   * idempotent; inert when C5 is off or emission was never armed.
   */
  startUrlWatch(): void {
    if (!this.active || !this.collectionEnabled) return;
    if (this.urlWatchTimer || this.popstateHandler) return;
    if (typeof window === 'undefined') return;
    const report = () => this.pageViewed(this.currentPath());
    this.popstateHandler = report;
    window.addEventListener('popstate', this.popstateHandler);
    this.urlWatchTimer = setInterval(report, 2000);
  }

  /** Unbind the popstate listener and stop the poll (teardown / consent-off). */
  stopUrlWatch(): void {
    if (this.popstateHandler && typeof window !== 'undefined') {
      window.removeEventListener('popstate', this.popstateHandler);
    }
    this.popstateHandler = null;
    if (this.urlWatchTimer) {
      clearInterval(this.urlWatchTimer);
      this.urlWatchTimer = null;
    }
  }

  flush(useBeacon: boolean): void {
    this.queue.flush(useBeacon);
  }

  dispose(): void {
    // Teardown is final for this client instance. Abort before the queue's
    // final flush so a token-less batch cannot start another mint while the
    // old one is being invalidated.
    // The page visit's last summary rides the queue's final flush; it is
    // queued while emission is still armed, then the page stops being watched.
    this.queuePageSummary();
    this.stopPageEngagement();
    this.active = false;
    this.invalidateMint();
    this.stopUrlWatch();
    this.queue.dispose();
  }

  private buildEvent(name: SdkVisitorEventName, props?: Record<string, unknown>): VisitorEventItem {
    return {
      clientEventId: newClientEventId(),
      name,
      ...(props && Object.keys(props).length > 0 ? { props } : {}),
      page: this.currentPath(),
      ts: Date.now(),
    };
  }

  private currentPath(): string {
    try {
      return typeof location !== 'undefined' ? location.pathname : '';
    } catch {
      return '';
    }
  }

  /**
   * Queue transport. With a stored token this is the plain keepalive-fetch
   * path. Without one, token acquisition is SERIALIZED on the single shared
   * latch in visitor-token.ts: if an acquisition is in flight (the bootstrap,
   * an earlier flush, or wa-resume's conversation open) this batch waits for
   * it, re-stamps itself with the acquired token, and sends normally; only
   * when there is no acquisition at all does this batch become the minting
   * request itself (a response-reading keepalive fetch — a beacon can't read
   * the minted token). Concurrent token-less fetches would each make the
   * server mint a fresh WebVisitor and split the person's events across
   * identities.
   */
  private send(url: string, body: string, useBeacon: boolean): void {
    if (this.options.send) {
      this.options.send(url, body, useBeacon);
      return;
    }
    if (getStoredVisitorToken()) {
      sendEventBatch(url, body, useBeacon);
      return;
    }
    this.sendTokenless(url, body);
  }

  /**
   * Token-less delivery: join the shared in-flight acquisition if one exists,
   * otherwise become it. On settle each joined batch re-checks the world —
   * a stored token means the acquisition won and the batch re-stamps + sends;
   * no token but a NEWER acquisition in flight means another joined batch (or
   * a conversation open) already started the replacement, so this one joins
   * that instead of racing a parallel mint (several batches can join the SAME
   * failed acquisition, and each starting its own replacement would split the
   * person's identity); only when there is no acquisition at all does this
   * batch become the replacement.
   */
  private sendTokenless(url: string, body: string): void {
    // A drained batch may be waiting on the shared acquisition when consent is
    // withdrawn or this client is disposed. Its continuation must stay dead —
    // otherwise settling the cancelled mint immediately starts a replacement.
    if (!this.active || !this.collectionEnabled) return;
    const pending = pendingTokenAcquisition();
    if (!pending) {
      this.startMint(body, url);
      return;
    }
    const rejoin = () => {
      if (!this.active || !this.collectionEnabled) return;
      const token = getStoredVisitorToken();
      if (token) {
        sendEventBatch(url, this.withToken(body, token), false);
      } else {
        this.sendTokenless(url, body);
      }
    };
    pending.then(rejoin, rejoin);
  }

  /** Re-stamp an already-serialized batch body with the (post-mint) token. */
  private withToken(body: string, token: string): string {
    try {
      const parsed = JSON.parse(body) as Record<string, unknown>;
      parsed.visitorToken = token;
      return JSON.stringify(parsed);
    } catch {
      return body;
    }
  }

  /**
   * Fire the token-capturing fetch and register it as THE shared in-flight
   * acquisition (visitor-token.ts) so every concurrent token-less path —
   * including wa-resume's conversation open — joins it. The latch clears
   * itself on settle (only if still current). Never throws into the host page.
   */
  private startMint(body: string, url: string = this.endpoint()): void {
    if (!this.active || !this.collectionEnabled) return;
    try {
      const fetchFn = this.options.fetchFn ?? fetch;
      const generation = ++this.mintGeneration;
      const controller = new AbortController();
      this.mintAbortController = controller;
      const settle = beginTokenAcquisition();
      void (async () => {
        const timeoutId = setTimeout(
          () => controller.abort(),
          VISITOR_TOKEN_MINT_TIMEOUT_MS,
        );
        try {
          // Race the WHOLE response read, not only fetch(). A proxy can deliver
          // headers and then wedge the JSON body; relying solely on fetch's
          // AbortSignal would leave the shared acquisition latch pinned when a
          // custom transport ignores abort. The underlying promise is still
          // observed by Promise.race, so a late reject cannot become unhandled.
          const request = (async (): Promise<unknown> => {
            const response = await fetchFn(url, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body,
              keepalive: true,
              // Anonymous, clientKey-authenticated ingest — no cookies, ever.
              credentials: 'omit',
              signal: controller.signal,
            });
            if (!response.ok) return null;
            return response.json().catch(() => null);
          })();
          const aborted = new Promise<never>((_resolve, reject) => {
            const rejectAbort = () => {
              const error = new Error('Visitor token mint aborted');
              error.name = 'AbortError';
              reject(error);
            };
            if (controller.signal.aborted) rejectAbort();
            else
              controller.signal.addEventListener('abort', rejectAbort, {
                once: true,
              });
          });
          const result = await Promise.race([request, aborted]);

          // Consent/teardown can race both the response headers and JSON body.
          // A generation mismatch makes that late response observationally
          // dead even when the injected/browser transport ignored abort.
          if (
            generation === this.mintGeneration &&
            this.active &&
            this.collectionEnabled &&
            !controller.signal.aborted
          ) {
            storeVisitorToken(
              (result as { data?: { visitorToken?: unknown } } | null)?.data?.visitorToken,
            );
          }
        } catch (e) {
          // Abort is an expected timeout/consent/teardown outcome. Other
          // transport failures remain visible, but never escape into the host.
          if (!controller.signal.aborted) {
            this.logger.warn('[VisitorEvents] token mint failed:', e);
          }
        } finally {
          clearTimeout(timeoutId);
          if (this.mintAbortController === controller) {
            this.mintAbortController = null;
          }
          settle();
        }
      })();
    } catch {
      /* analytics must never throw into the host page */
    }
  }

  /** Abort + invalidate only the mint this client owns. The module-wide latch
   *  may instead belong to wa-resume, which this client must never cancel. */
  private invalidateMint(): void {
    this.mintGeneration += 1;
    const controller = this.mintAbortController;
    this.mintAbortController = null;
    controller?.abort();
  }
}
